import { gunzipSync } from 'fflate';
import { CONTENT_PACK_SCHEMA_SQL, parseManifestV2 } from './contentPackSchema';
import { sha256Hex } from './hash';
import { inspectSqlite, loadSqlJs, validateContentDatabase } from './sqliteContentWriter';
import type { ContentManifestV2 } from './types';

const MERGE_TABLES = ['content_packs', 'categories', 'puzzles', 'puzzle_aliases', 'puzzle_answer_words', 'puzzle_tokens', 'puzzle_word_groups', 'equivalence_rules'] as const;

function rows(db: import('sql.js').Database, table: string): Record<string, unknown>[] {
  const result = db.exec(`SELECT * FROM ${table} ORDER BY rowid`)[0];
  if (!result) return [];
  return result.values.map((values) => Object.fromEntries(result.columns.map((column, index) => [column, values[index]])));
}

function rowSignature(row: Record<string, unknown>): string { return JSON.stringify(Object.entries(row).sort(([a], [b]) => a.localeCompare(b))); }

export interface SeedBuildResult { bytes: Uint8Array; sha256: string; packCount: number; puzzleCount: number; manifestGeneratedAt: string; }

export async function buildSeedFromManifest(value: unknown, fetcher: typeof fetch = fetch): Promise<SeedBuildResult> {
  const manifest: ContentManifestV2 = parseManifestV2(value);
  const active = manifest.packs.filter((pack) => pack.status === 'active');
  if (active.length === 0) throw new Error('Le manifeste ne contient aucun pack actif.');
  const SQL = await loadSqlJs();
  const seed = new SQL.Database();
  const seen = new Map<string, Map<string, string>>();
  try {
    seed.run(CONTENT_PACK_SCHEMA_SQL);
    seed.run('BEGIN');
    let puzzleCount = 0;
    for (const pack of active) {
      const response = await fetcher(pack.url);
      if (!response.ok) throw new Error(`Pack inaccessible : ${pack.id} (${response.status}).`);
      const compressed = new Uint8Array(await response.arrayBuffer());
      if (compressed.byteLength !== pack.sizeBytes) throw new Error(`Taille incorrecte pour ${pack.id}.`);
      if (await sha256Hex(compressed) !== pack.sha256) throw new Error(`SHA-256 incorrect pour ${pack.id}.`);
      const uncompressed = gunzipSync(compressed);
      if (uncompressed.byteLength !== pack.uncompressedSizeBytes) throw new Error(`Taille décompressée incorrecte pour ${pack.id}.`);
      const source = await inspectSqlite(uncompressed);
      try {
        const metadata = Object.fromEntries((source.exec('SELECT key,value FROM metadata')[0]?.values ?? []).map((row) => [String(row[0]), String(row[1])]));
        if (metadata.content_schema_version !== '2' || metadata.kind !== 'pack') throw new Error(`Métadonnées de pack invalides : ${pack.id}.`);
        if (metadata.content_pack_id !== pack.id || Number(metadata.pack_version) !== pack.version) throw new Error(`Identité/version SQLite incohérente : ${pack.id}.`);
        const actualPuzzleCount = Number(source.exec('SELECT count(*) FROM puzzles')[0]?.values[0]?.[0] ?? -1);
        if (actualPuzzleCount !== pack.puzzleCount) throw new Error(`Nombre d’énigmes incohérent : ${pack.id}.`);
        for (const table of MERGE_TABLES) {
          const tableSeen = seen.get(table) ?? new Map<string, string>(); seen.set(table, tableSeen);
          for (const row of rows(source, table)) {
            const id = String(row.id); const signature = rowSignature(row); const previous = tableSeen.get(id);
            if (previous && previous !== signature) throw new Error(`Définition incompatible : ${table}.${id}.`);
            if (previous) continue;
            tableSeen.set(id, signature);
            const columns = Object.keys(row); seed.run(`INSERT INTO ${table} (${columns.join(',')}) VALUES (${columns.map(() => '?').join(',')})`, columns.map((column) => row[column] as import('sql.js').SqlValue));
            if (table === 'puzzles') puzzleCount += 1;
          }
        }
      } finally { source.close(); }
    }
    const generatedAt = new Date().toISOString();
    const metadata = [['content_schema_version', '2'], ['kind', 'seed'], ['content_release_id', String(manifest.releaseId)], ['generated_at', generatedAt], ['pack_count', String(active.length)], ['puzzle_count', String(puzzleCount)]];
    for (const [key, value] of metadata) seed.run('INSERT INTO metadata(key,value) VALUES (?,?)', [key, value]);
    seed.run('COMMIT'); validateContentDatabase(seed);
    const bytes = seed.export();
    return { bytes, sha256: await sha256Hex(bytes), packCount: active.length, puzzleCount, manifestGeneratedAt: manifest.generatedAt };
  } catch (error) { try { seed.run('ROLLBACK'); } catch { /* ignored */ } throw error; }
  finally { seed.close(); }
}
